import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  IoArrowBackOutline,
  IoCalendarOutline,
  IoTimeOutline,
  IoShareSocialOutline,
} from "react-icons/io5";
import moment from "moment";
import Axios from "../../Axios";
import Layout from "../../utils/Layouts/Layout/Layout";
import Header from "../../utils/Header/Header";
import "./newsDetail.scss";

const fallbackImg =
  "https://images.unsplash.com/photo-1495020689067-958852a7765e?q=80&w=1400&auto=format&fit=crop";

const stripHtml = (html = "") =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const NewsDetail = () => {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    Axios.get(`news/${id}`)
      .then((res) => {
        if (res?.data) {
          setData(res.data);
        }
      })
      .catch((err) => {
        console.error("News detail error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const createdDate = useMemo(() => {
    return data?.created_at ? moment(data.created_at).format("YYYY.MM.DD") : "";
  }, [data]);

  const readingTime = useMemo(() => {
    const plainText = stripHtml(data?.content || "");
    const words = plainText.split(" ").filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / 180));
    return `${minutes} мин уншина`;
  }, [data]);

  const summary = useMemo(() => {
    const plainText = stripHtml(data?.content || "");
    return plainText.slice(0, 180) + (plainText.length > 180 ? "..." : "");
  }, [data]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: data?.title || "Мэдээ",
          text: summary,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Линк хуулагдлаа");
      }
    } catch (error) {
      console.error("Share error:", error);
    }
  };

  return (
    <Layout>
      <Header onHome={true} />

      <div className="news-editorial">
        <div className="news-editorial__container">
          {loading ? (
            <>
              <div className="news-editorial__topbar">
                <div className="news-editorial__skeleton news-editorial__skeleton--btn" />
              </div>

              <section className="news-editorial__hero news-editorial__hero--loading">
                <div className="news-editorial__hero-left">
                  <div className="news-editorial__skeleton news-editorial__skeleton--badge" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--title" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--title short" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--text" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--text short" />
                  <div className="news-editorial__hero-meta">
                    <div className="news-editorial__skeleton news-editorial__skeleton--meta" />
                    <div className="news-editorial__skeleton news-editorial__skeleton--meta" />
                  </div>
                </div>

                <div className="news-editorial__hero-right">
                  <div className="news-editorial__skeleton news-editorial__skeleton--image" />
                </div>
              </section>

              <section className="news-editorial__body">
                <div className="news-editorial__content-card">
                  <div className="news-editorial__skeleton news-editorial__skeleton--line" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--line" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--line short" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--line" />
                  <div className="news-editorial__skeleton news-editorial__skeleton--line" />
                </div>

                <div className="news-editorial__sidebar">
                  <div className="news-editorial__side-card">
                    <div className="news-editorial__skeleton news-editorial__skeleton--side-title" />
                    <div className="news-editorial__skeleton news-editorial__skeleton--side-text" />
                  </div>
                </div>
              </section>
            </>
          ) : (
            <>
              <div className="news-editorial__topbar">
                <Link to="/news" className="news-editorial__back">
                  <IoArrowBackOutline />
                  <span>Мэдээний жагсаалт</span>
                </Link>
              </div>

              <section className="news-editorial__hero">
                <div className="news-editorial__hero-left">
                  <div className="news-editorial__badge">Мэдээ мэдээлэл</div>

                  <h1 className="news-editorial__title">
                    {data?.title || "Гарчиг байхгүй"}
                  </h1>

                  <p className="news-editorial__summary">
                    {summary || "Мэдээний товч тайлбар энд харагдана."}
                  </p>

                  <div className="news-editorial__hero-meta">
                    <div className="news-editorial__meta-chip">
                      <IoCalendarOutline />
                      <span>{createdDate}</span>
                    </div>

                    <div className="news-editorial__meta-chip">
                      <IoTimeOutline />
                      <span>{readingTime}</span>
                    </div>
                  </div>
                </div>

                <div className="news-editorial__hero-right">
                  <div className="news-editorial__hero-image-wrap">
                    <img
                      src={data?.cover || fallbackImg}
                      alt={data?.title || "news"}
                      className="news-editorial__hero-image"
                    />
                  </div>
                </div>
              </section>

              <section className="news-editorial__body">
                <article className="news-editorial__content-card">
                  <div
                    className="news-editorial__content"
                    dangerouslySetInnerHTML={{ __html: data?.content || "" }}
                  />
                </article>

                <aside className="news-editorial__sidebar">
                  <div className="news-editorial__side-card">
                    <span className="news-editorial__side-label">
                      Нийтлэгдсэн
                    </span>
                    <h4>{createdDate}</h4>
                    <p>Энэ мэдээ нийтлэгдсэн огноо</p>
                  </div>

                  <div className="news-editorial__side-card">
                    <span className="news-editorial__side-label">
                      Унших хугацаа
                    </span>
                    <h4>{readingTime}</h4>
                    <p>Дундаж унших хугацааны тооцоо</p>
                  </div>

                  <div className="news-editorial__side-card news-editorial__side-card--accent">
                    <span className="news-editorial__side-label">
                      Хуваалцах
                    </span>
                    <h4>Энэ мэдээг бусадтай хуваалцаарай</h4>
                    <button
                      type="button"
                      className="news-editorial__share-btn"
                      onClick={handleShare}
                    >
                      <IoShareSocialOutline />
                      <span>Линк хуулах / хуваалцах</span>
                    </button>
                  </div>
                </aside>
              </section>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NewsDetail;
